import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { formatDataGeneral, getFileLinkIfExist } from "utils/utils";
import { renderDpatEvidenceLink, scoreOnlyIfDocumentLinksComplete } from "utils/renderDpatEvidenceLink";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { documentsColumn } from "utils/tableColums";

const DOCUMENT_HUB_FILE_ELEMENT = "MjPmcbueEdU";

function fileUrl(eventId, dataElementUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function buildSocialProtectionDocLinks(documents) {
  const documentData = documents?.data;
  const documentReports = documents?.reports;
  const documentTypes = [
    {
      type: "Annual Action Plan Progress Report",
      title: "Annual Action Plan Progress Report",
    },
    {
      type: "Medium Term Development Plan (MTDP)",
      title: "Meduim Term Development Plan Document",
    },
    { type: "Social Welfare Progress Report", title: "Social Welfare Progress Report" },
    {
      type: "Social Services activity implementation Reports",
      title: "Social Services activity implementation Reports",
    },
  ];

  return documentTypes.map(({ type, title }) => {
    const matchedDocs = formatDataGeneral(
      documentData,
      "DPAT | M&E Tool Type",
      type,
    ) || [];

    let reportLink = "";
    if (matchedDocs.length > 0) {
      reportLink = getFileLinkIfExist(
        documentReports,
        DOCUMENT_HUB_FILE_ELEMENT,
        matchedDocs[0]?.trackedEntity,
      );
    }

    return {
      title,
      link: reportLink
        ? fileUrl(reportLink, DOCUMENT_HUB_FILE_ELEMENT)
        : "Not Uploaded",
    };
  });
}

const SocialProtectionServices = forwardRef(({
  year,
  services,
  documents,
  districtId,
  hideComment,
  savedAssessmentData,
  isAssessmentStarted,
}, ref) => {
  const [scorei, setScorei] = useState(0);
  const [scoreii, setScoreii] = useState(0);
  const [scoreiii, setScoreiii] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [maxScore, setMaxScore] = useState(3);

  const { Header, Content } = Layout;
  const { Title, Text } = Typography;

  const resolved = useMemo(() => {
    if (!isAssessmentStarted || !savedAssessmentData) return null;
    return {
      services: savedAssessmentData.services ?? services,
      scorei: savedAssessmentData.scorei ?? 0,
      scoreii: savedAssessmentData.scoreii ?? 0,
      scoreiii: savedAssessmentData.scoreiii ?? 0,
      percentage: savedAssessmentData.percentage ?? 0,
      maxScore: savedAssessmentData.maxScore ?? 3,
    };
  }, [isAssessmentStarted, savedAssessmentData, services]);

  const displayServices = resolved?.services ?? services;
  const displayScorei = resolved?.scorei ?? scorei;
  const displayScoreii = resolved?.scoreii ?? scoreii;
  const displayScoreiii = resolved?.scoreiii ?? scoreiii;
  const displayPercentage = resolved?.percentage ?? percentage;
  const displayMaxScore = resolved?.maxScore ?? maxScore;

  const liveDocumentLinks = useMemo(
    () => buildSocialProtectionDocLinks(documents),
    [documents],
  );

  const displayDocumentLinks = isAssessmentStarted && savedAssessmentData?.links?.length
    ? savedAssessmentData.links
    : liveDocumentLinks;

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
          ...col,
          render: (value) => renderDpatEvidenceLink(value, "View Document"),
        }
        : col
    ),
    [],
  );

  useEffect(() => {
    if (isAssessmentStarted) return;

    let nextScorei = 0;
    let nextScoreii = 0;
    let nextScoreiii = 0;

    if (services?.aap?.length > 0) {
      setPercentage(services.aap[0].percentage);

      if (services.aap[0].percentage >= 60) {
        nextScoreii = 1;
        nextScoreiii = 1;
      }
    }

    if (services?.publication?.length > 0) {
      nextScorei = 1;
    }

    setScorei(scoreOnlyIfDocumentLinksComplete(displayDocumentLinks, nextScorei));
    setScoreii(scoreOnlyIfDocumentLinksComplete(displayDocumentLinks, nextScoreii));
    setScoreiii(scoreOnlyIfDocumentLinksComplete(displayDocumentLinks, nextScoreiii));
  }, [year, districtId, services, isAssessmentStarted, displayDocumentLinks]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI4",
      area: "Social Protection, Gender and Nutrition",
      maxScore: displayMaxScore,
      services: displayServices,
      links: displayDocumentLinks,
      scorei: displayScorei,
      scoreii: displayScoreii,
      scoreiii: displayScoreiii,
      percentage: displayPercentage,
    }),
  }));

  const DPAT_SLUG = "sdi4.0-4.1";

  const aapColumn = [
    { title: "a. Number of activities in AAP", dataIndex: "aapTotal", key: "aapTotal" },
    { title: "b. Number of Social Protection activities in AAP", dataIndex: "aapSocialProtection", key: "aapSocialProtection" },
    { title: "c. Number of Social Protection activities in AAP implemented", dataIndex: "aapSocialProtectionImp", key: "aapSocialProtectionImp" },
    { title: "d. Percentage of Social Protection activities implemented (c/b x 100)", dataIndex: "percentage", key: "percentage" }
  ];

  const publicationColumn = [
    { title: "List of Social Services Available (Yes/No)", dataIndex: "list", key: "list" },
    { title: "List of Social Services published on Notice Boards or Website (Yes/No)", dataIndex: "publication", key: "publication" },
    { title: "Web site Link", dataIndex: "website", key: "website" },
    { title: "Summary", dataIndex: "summary", key: "summary" }
  ];

  return (
    <Comment
      data={displayServices?.aap}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi4.0-4.1-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
          <MaxScoreDisplay score={displayMaxScore} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 4.0-4.1i Actual Score:" score={displayScorei} />
          <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 4.0-4.1ii Actual Score:" score={displayScoreii} />
          <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 4.0-4.1iii Actual Score:" score={displayScoreiii} />
          </IndicatorScoreSection>
          <Title level={4} style={{ marginTop: "20px" }}>I- Evidence of Dissemination of Social Services to Citizens</Title>
          <Table
            columns={publicationColumn}
            dataSource={displayServices?.publication || []}
            pagination={false} bordered />

          <Title level={4} style={{ marginTop: "20px" }}>II- Evidence of implementation of action plans on social protection</Title>
          {<Table
            columns={aapColumn}
            dataSource={displayServices?.aap}
            pagination={false} bordered />}

          <Title level={4} style={{ marginTop: "20px" }}>III- Evidence of Attached Documents</Title>
          <Table
            columns={documentTableColumns}
            dataSource={displayDocumentLinks}
            rowKey="title"
            pagination={false}
            bordered
          />

          <Title level={5} style={{ marginTop: "30px" }}>Conclusion</Title>
          <Content>
            {displayPercentage}% of Social Protection activities in the Annual Action Plan were implemented
          </Content>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default SocialProtectionServices;